import React, { useState } from "react";
import { GBPFormatter } from "../../../utils";
import { formatDate } from "../../../modules/dates";
import * as ROUTES from "../../../Router/corporate_routes";
import { Link } from "react-router-dom";

const PREFERRED_PROGRAMME_KEY = "preferredProgramme";
const NOT_IN_PREFERRED_PROGRAMME_KEY = "notInPreferredProgramme";

const PreferredProgrammeSpendReportTable = ({ report = {} }) => {
  const [showProgrammeSections, setShowProgrammeSections] = useState({
    [PREFERRED_PROGRAMME_KEY]: false,
    [NOT_IN_PREFERRED_PROGRAMME_KEY]: false,
  });

  const [showGroupRows, setShowGroupRows] = useState({});
  const [showBrandRows, setShowBrandRows] = useState({});
  const [showVenueRows, setShowVenueRows] = useState({});
  const [showEventRows, setShowEventRows] = useState({});

  const toggleProgramme = (programmeKey) => {
    setShowProgrammeSections(prev => ({
      ...prev,
      [programmeKey]: !prev[programmeKey],
    }));
  };

  const toggleGroupRows = (programmeKey, groupId) => {
    const key = `${programmeKey}:${groupId}`;
    setShowGroupRows(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleBrandRows = (programmeKey, groupId, brandId) => {
    const key = `${programmeKey}:${groupId}:${brandId}`;
    setShowBrandRows(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleVenueRows = (programmeKey, groupId, brandId, venueId) => {
    const key = `${programmeKey}:${groupId}:${brandId}:${venueId}`;
    setShowVenueRows(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleEventRows = (programmeKey, groupId, brandId, venueId) => {
    const key = `${programmeKey}:${groupId}:${brandId}:${venueId}`;
    setShowEventRows(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const renderProgrammeSection = (programmeKey) => {
    const programmeData = report[programmeKey];
    if (!programmeData || !programmeData.groups) return null;

    const programmeTitle =
      programmeKey === PREFERRED_PROGRAMME_KEY
        ? "Preferred Programme Total"
        : "Other Spend Total";

    const isOpen = showProgrammeSections[programmeKey];

    return (
      <>
        <tr
          className="pointer hover:bg-light-grey border-b border-grey"
          onClick={() => toggleProgramme(programmeKey)}
        >
          <td className="font-bold py-2">
            <i
              className={`fas mx-2 text-xs ${
                isOpen ? "fa-minus-square" : "fa-plus-square"
              }`}
            />
            {programmeTitle}
          </td>
          <td className="text-right font-bold">
            {GBPFormatter.format(programmeData.totals.netTotal)}
          </td>
          <td className="text-right font-bold">
            {programmeData.totals.marketShare?.overall || 0}%
          </td>
          <td className="text-right font-bold">
            {GBPFormatter.format(programmeData.totals.costSavings)}
          </td>
          <td className="text-right font-bold">
            {programmeData.totals.percentageCostSavings}%
          </td>
        </tr>

        {isOpen &&
          programmeData.groups.map((group) => (
            <GroupRow
              key={`${programmeKey}-${group.id}`}
              programmeKey={programmeKey}
              group={group}
              showGroupRows={showGroupRows}
              toggleGroupRows={toggleGroupRows}
              showBrandRows={showBrandRows}
              toggleBrandRows={toggleBrandRows}
              showVenueRows={showVenueRows}
              toggleVenueRows={toggleVenueRows}
              showEventRows={showEventRows}
              toggleEventRows={toggleEventRows}
            />
          ))}
      </>
    );
  };

  return (
    <table className="w-full">
      <thead>
      <tr>
        <td className="font-bold py-2 border-b border-grey">Venue / Group</td>
        <td className="font-bold py-2 border-b border-grey text-right">Net Value</td>
        <td className="font-bold py-2 border-b border-grey text-right">Market Share</td>
        <td className="font-bold py-2 border-b border-grey text-right">Net Cost Savings</td>
        <td className="font-bold py-2 border-b border-grey text-right">Savings %</td>
      </tr>
      </thead>
      <tbody>
      {renderProgrammeSection(PREFERRED_PROGRAMME_KEY)}
      {renderProgrammeSection(NOT_IN_PREFERRED_PROGRAMME_KEY)}
      </tbody>
    </table>
  );
};

const GroupRow = ({
                    programmeKey,
                    group,
                    showGroupRows,
                    toggleGroupRows,
                    showBrandRows,
                    toggleBrandRows,
                    showVenueRows,
                    toggleVenueRows,
                    showEventRows,
                    toggleEventRows,
                  }) => {
  const groupKey = `${programmeKey}:${group.id}`;
  const isOpen = showGroupRows[groupKey];

  return (
    <>
      <tr
        className="pointer hover:bg-light-grey border-b border-grey"
        onClick={() => toggleGroupRows(programmeKey, group.id)}
      >
        <td className="pl-4 py-2">
          <i
            className={`fas mx-2 text-xs ${
              isOpen ? "fa-minus-square" : "fa-plus-square"
            }`}
          />
          <strong>{group.name}</strong>
        </td>
        <td className="text-right">
          {GBPFormatter.format(group.totals.netTotal)}
        </td>
        <td className="text-right">
          {parseFloat(group.totals.marketShare?.overall || 0)}%
        </td>
        <td className="text-right">
          {GBPFormatter.format(group.totals.costSavings)}
        </td>
        <td className="text-right">
          {group.totals.percentageCostSavings}%
        </td>
      </tr>

      {isOpen &&
        group.brands.map((brand) => (
          <BrandRow
            key={`${groupKey}-${brand.id}`}
            programmeKey={programmeKey}
            groupId={group.id}
            brand={brand}
            showBrandRows={showBrandRows}
            toggleBrandRows={toggleBrandRows}
            showVenueRows={showVenueRows}
            toggleVenueRows={toggleVenueRows}
            showEventRows={showEventRows}
            toggleEventRows={toggleEventRows}
          />
        ))}
    </>
  );
};

const BrandRow = ({
                    programmeKey,
                    groupId,
                    brand,
                    showBrandRows,
                    toggleBrandRows,
                    showVenueRows,
                    toggleVenueRows,
                    showEventRows,
                    toggleEventRows,
                  }) => {
  const brandKey = `${programmeKey}:${groupId}:${brand.id}`;
  const isOpen = showBrandRows[brandKey];

  return (
    <>
      <tr
        className="pointer hover:bg-light-grey border-b border-grey"
        onClick={() => toggleBrandRows(programmeKey, groupId, brand.id)}
      >
        <td className="pl-6 py-2">
          <i
            className={`fas mx-2 text-xs ${
              isOpen ? "fa-minus-square" : "fa-plus-square"
            }`}
          />
          <strong>{brand.name}</strong>
        </td>
        <td className="text-right">
          {GBPFormatter.format(brand.totals.netTotal)}
        </td>
        <td className="text-right">
          {parseFloat(brand.totals.marketShare?.overall || 0)}%
        </td>
        <td className="text-right">
          {GBPFormatter.format(brand.totals.costSavings)}
        </td>
        <td className="text-right">
          {brand.totals.percentageCostSavings}%
        </td>
      </tr>

      {isOpen &&
        brand.venues.map((venue) => (
          <VenueRow
            key={`${brandKey}-${venue.id}`}
            programmeKey={programmeKey}
            groupId={groupId}
            brandId={brand.id}
            venue={venue}
            showVenueRows={showVenueRows}
            toggleVenueRows={toggleVenueRows}
            showEventRows={showEventRows}
            toggleEventRows={toggleEventRows}
          />
        ))}
    </>
  );
};

const VenueRow = ({
                    programmeKey,
                    groupId,
                    brandId,
                    venue,
                    showVenueRows,
                    toggleVenueRows,
                    showEventRows,
                    toggleEventRows,
                  }) => {
  const venueKey = `${programmeKey}:${groupId}:${brandId}:${venue.id}`;
  const isOpen = showVenueRows[venueKey];

  return (
    <>
      <tr
        className="pointer hover:bg-light-grey border-b border-grey py-2"
        onClick={() => toggleVenueRows(programmeKey, groupId, brandId, venue.id)}
      >
        <td className="py-2 pl-8">
          <i
            className={`fas mx-2 text-xs ${
              isOpen ? "fa-minus-square" : "fa-plus-square"
            }`}
          />
          <strong>{venue.name}</strong>
        </td>
        <td className="text-right">
          {GBPFormatter.format(venue.totals.netTotal)}
        </td>
        <td className="text-right">
          {parseFloat(venue.totals.marketShare?.overall || 0)}%
        </td>
        <td className="text-right">
          {GBPFormatter.format(venue.totals.costSavings)}
        </td>
        <td className="text-right">
          {venue.totals.percentageCostSavings}%
        </td>
      </tr>

      {isOpen &&
        venue.events.map((eventEntry) => (
          <tr key={eventEntry.id} className="hover:bg-light-grey text-sm">
            <td className="py-1 pl-12">
              <Link
                className="text-black p-2 hover:underline"
                to={ROUTES.FRONTEND.BOOKED_EVENTS.SINGLE.replace(
                  ":slug",
                  eventEntry.event.id,
                )}
              >
                <strong>
                  {eventEntry.event.ref_id} - {eventEntry.event.name}
                </strong>{" "}
                - {formatDate(eventEntry.event.event_date_from, "DD/MM/YYYY")}
              </Link>
            </td>
            <td className="text-right">
              {GBPFormatter.format(eventEntry.netTotal)}
            </td>
            <td className="text-right">
              {parseFloat(eventEntry.marketShare?.overall || 0)}%
            </td>
            <td className="text-right">
              {GBPFormatter.format(eventEntry.netCostSavings)}
            </td>
            <td className="text-right">
              {eventEntry.percentageCostSavings}%
            </td>
          </tr>
        ))}
    </>
  );
};

export default PreferredProgrammeSpendReportTable;